import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  AppRoutes,
  AppVersion,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  AuthRoutes,
  DateFormats,
  formatDateToString,
  LegalRoutes,
  ProfileRoutes,
  UserModel,
  UserStateService
} from '@core';
import { SzBrandBlockComponent, SzIconComponent } from '@ui';

@Component({
  selector: 'app-profile-page',
  templateUrl: './profile.page.html',
  styleUrls: ['./profile.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, RouterLink, SzBrandBlockComponent, SzIconComponent]
})
export class ProfilePage {
  readonly editPath: string = `/${AppRoutes.PROFILE}/${ProfileRoutes.EDIT}`;
  readonly contactPath: string = `/${AppRoutes.PROFILE}/${ProfileRoutes.CONTACT}`;
  readonly privacyPath: string = `/${AppRoutes.LEGAL}/${LegalRoutes.PRIVACY}`;
  readonly termsPath: string = `/${AppRoutes.LEGAL}/${LegalRoutes.TERMS}`;
  readonly appVersion: string = AppVersion;

  private readonly authClient: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);
  private readonly userState: UserStateService = inject(UserStateService);
  private readonly router: Router = inject(Router);

  private readonly user: Signal<UserModel | null> = this.userState.user;
  readonly isAdmin: Signal<boolean> = this.userState.isAdmin;

  readonly displayName: Signal<string> = computed((): string => {
    const name: string = (this.user()?.displayName ?? '').trim();
    return name.length > 0 ? name : '-';
  });

  readonly email: Signal<string> = computed((): string => {
    const e: string = (this.user()?.email ?? '').trim();
    return e.length > 0 ? e : '-';
  });

  // TODO: tymczasowo wymuszony fallback (inicjały) - avatar z photoUrl wyłączony do naprawy.
  // Przywrócić: return url && url.trim().length > 0 ? url : null;
  readonly avatarUrl: Signal<string | null> = computed((): string | null => null);

  readonly initial: Signal<string> = computed((): string => {
    const name: string = (this.user()?.displayName ?? '').trim();
    if (name.length === 0) return 'SZ';
    const parts: ReadonlyArray<string> = name.split(/\s+/).filter((p: string): boolean => p.length > 0);
    if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    return parts[0].charAt(0).toUpperCase();
  });

  readonly memberSinceMonthYear: Signal<string | null> = computed((): string | null => {
    const iso: string | undefined = this.user()?.createdAt;
    if (!iso) return null;
    const date: Date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return formatDateToString(date, DateFormats.MonthYear);
  });

  readonly memberSinceFullDate: Signal<string | null> = computed((): string | null => {
    const iso: string | undefined = this.user()?.createdAt;
    if (!iso) return null;
    const date: Date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return formatDateToString(date, DateFormats.DayMonthYearDot);
  });

  goBack(): void {
    void this.router.navigateByUrl('/');
  }

  goContact(): void {
    void this.router.navigate([AppRoutes.PROFILE, ProfileRoutes.CONTACT]);
  }

  goStats(): void {
    void this.router.navigate([AppRoutes.ADMIN]);
  }

  async onSignOut(): Promise<void> {
    await this.authClient.signOut();
    await this.router.navigateByUrl(`/${AppRoutes.AUTH}/${AuthRoutes.WELCOME}`);
  }
}
